import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import * as ShoppingListActions from './shopping-list.actions';
import { ShoppingListService } from '../../services/shopping-list.service';
import { NotificationService } from '../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';

export const loadShoppingListItems$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.loadShoppingListItems),
      mergeMap(() =>
        service.getShoppingListItems().pipe(
          map(items => {
            // Optional: notify success
            // notification.success('Shopping list items loaded.');
            return ShoppingListActions.loadShoppingListItemsSuccess({ items });
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.loadFailed'));
            return of(ShoppingListActions.loadShoppingListItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadShoppingListQuota$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.loadShoppingListQuota),
      switchMap(() =>
        service.getShoppingListQuota().pipe(
          map(quota => {
            // Optional: notify success
            // notification.success('Shopping list quota loaded.');
            return ShoppingListActions.loadShoppingListQuotaSuccess({ quota });
          }),
          catchError(() => {
            notification.error(translate.instant('shoppingList.notifications.quotaFailed'));
            return of(ShoppingListActions.loadShoppingListQuotaFailure());
          })
        )
      )
    ),
  { functional: true }
);

export const addShoppingListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.addShoppingListItem),
      mergeMap(({ item }) =>
        service.addShoppingListItem(item).pipe(
          map(created => {
            notification.success(translate.instant('shoppingList.notifications.addSuccess'));
            return ShoppingListActions.addShoppingListItemSuccess({ item: created });
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.addFailed'));
            return of(ShoppingListActions.addShoppingListItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);
  

export const updateShoppingListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.updateShoppingListItem),
      mergeMap(({ id, item }) =>
        service.updateShoppingListItem(id, item).pipe(
          map(updated => {
            notification.success(translate.instant('shoppingList.notifications.updateSuccess'));
            return ShoppingListActions.updateShoppingListItemSuccess({ item: updated });
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.updateFailed'));
            return of(ShoppingListActions.updateShoppingListItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);
  

export const deleteShoppingListItem$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.deleteShoppingListItem),
      mergeMap(({ id }) =>
        service.deleteShoppingListItem(id).pipe(
          map(() => {
            notification.success(translate.instant('shoppingList.notifications.deleteSuccess'));
            return ShoppingListActions.deleteShoppingListItemSuccess({ id });
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.deleteFailed'));
            return of(ShoppingListActions.deleteShoppingListItemFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const refreshShoppingQuotaOnAdd$ = createEffect((actions$ = inject(Actions)) =>
  actions$.pipe(
    ofType(ShoppingListActions.addShoppingListItemSuccess),
    map(() => ShoppingListActions.loadShoppingListQuota())
  ),
  { functional: true }
);

export const refreshShoppingQuotaOnDelete$ = createEffect((actions$ = inject(Actions)) =>
  actions$.pipe(
    ofType(ShoppingListActions.deleteShoppingListItemSuccess, ShoppingListActions.clearBoughtShoppingListItemsSuccess),
    map(() => ShoppingListActions.loadShoppingListQuota())
  ),
  { functional: true }
);

export const clearBoughtShoppingListItems$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.clearBoughtShoppingListItems),
      mergeMap(() =>
        service.clearBoughtItems().pipe(
          map(() => {
            notification.success(translate.instant('shoppingList.notifications.clearBoughtSuccess'));
            return ShoppingListActions.clearBoughtShoppingListItemsSuccess();
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.clearBoughtFailed'));
            return of(ShoppingListActions.clearBoughtShoppingListItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const bulkAddShoppingListItems$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ShoppingListService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(ShoppingListActions.bulkAddShoppingListItems),
      mergeMap(({ items }) =>
        service.bulkAddShoppingListItems(items).pipe(
          map(created => {
            notification.success(translate.instant('shoppingList.notifications.bulkAddSuccess'));
            return ShoppingListActions.bulkAddShoppingListItemsSuccess({ items: created });
          }),
          catchError(error => {
            notification.error(translate.instant('shoppingList.notifications.bulkAddFailed'));
            return of(ShoppingListActions.bulkAddShoppingListItemsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const ShoppingListEffects = {
  loadShoppingListItems$,
  loadShoppingListQuota$,
  addShoppingListItem$,
  updateShoppingListItem$,
  deleteShoppingListItem$,
  clearBoughtShoppingListItems$,
  refreshShoppingQuotaOnAdd$,
  refreshShoppingQuotaOnDelete$,
  bulkAddShoppingListItems$
};
